import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../services/api";
import Navbar from "../components/Navbar";

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancellingOrder, setCancellingOrder] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const userId = localStorage.getItem("userId");
      const response = await api.get(`/orders?userId=${userId}`);

      const normalizedOrders = response.data.map((order) => ({
        ...order,
        status: normalizeStatus(order.status),
      }));

      setOrders(normalizedOrders);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const normalizeStatus = (status) => {
    const statusMap = {
      "Chờ xác nhận": "pending",
      "Chờ thanh toán": "pending",
      "Đã xác nhận": "confirmed",
      "Đang giao": "shipping",
      "Đã giao": "delivered",
      "Đã hủy": "cancelled",
    };
    return statusMap[status] || status.toLowerCase();
  };

  const handleOpenCancelModal = (order) => {
    setSelectedOrder(order);
    setCancelReason("");
    setShowCancelModal(true);
  };

  const handleCloseCancelModal = () => {
    setShowCancelModal(false);
    setSelectedOrder(null);
    setCancelReason("");
  };

  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) {
      alert("Vui lòng chọn lý do hủy đơn");
      return;
    }

    setCancellingOrder(true);
    try {
      const response = await api.put(`/orders/${selectedOrder._id}/cancel`, {
        cancelReason: cancelReason,
        status: "Đã hủy",
      });

      if (response.data) {
        setOrders(orders.map((order) =>
          order._id === selectedOrder._id
            ? { ...order, status: "cancelled", cancelReason: cancelReason }
            : order
        ));
        alert("Đã hủy đơn hàng thành công!");
        handleCloseCancelModal();
      }
    } catch (error) {
      console.error("Error cancelling order:", error);
      alert("Không thể hủy đơn hàng. Vui lòng thử lại!");
    } finally {
      setCancellingOrder(false);
    }
  };

  const cancelReasons = [
    "Tôi muốn thay đổi địa chỉ giao hàng",
    "Tôi muốn thay đổi sản phẩm trong đơn hàng",
    "Tôi tìm thấy giá rẻ hơn ở chỗ khác",
    "Tôi đổi ý, không muốn mua nữa",
    "Thời gian giao hàng quá lâu",
    "Lý do khác",
  ];

  const formatPrice = (price) => new Intl.NumberFormat("vi-VN").format(price);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("vi-VN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "pending": return "bg-yellow-50 text-yellow-600 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-500 dark:border-yellow-900/30";
      case "confirmed": return "bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-900/20 dark:text-blue-500 dark:border-blue-900/30";
      case "shipping": return "bg-purple-50 text-purple-600 border-purple-200 dark:bg-purple-900/20 dark:text-purple-500 dark:border-purple-900/30";
      case "delivered": return "bg-green-50 text-green-600 border-green-200 dark:bg-green-900/20 dark:text-green-500 dark:border-green-900/30";
      case "cancelled": return "bg-red-50 text-red-600 border-red-200 dark:bg-red-900/20 dark:text-red-500 dark:border-red-900/30";
      default: return "bg-gray-50 text-gray-600 border-gray-200 dark:bg-slate-800 dark:text-gray-400 dark:border-slate-700";
    }
  };

  const getStatusText = (status) => {
    switch (status.toLowerCase()) {
      case "pending": return "Chờ xác nhận";
      case "confirmed": return "Đã xác nhận";
      case "shipping": return "Đang giao";
      case "delivered": return "Đã giao";
      case "cancelled": return "Đã hủy";
      default: return status;
    }
  };

  const filteredOrders = orders.filter((order) => {
    const matchStatus = filterStatus === "all" || order.status.toLowerCase() === filterStatus;
    const matchSearch = order._id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        order.customerName.toLowerCase().includes(searchTerm.toLowerCase());
    return matchStatus && matchSearch;
  });

  const getOrderCountByStatus = (status) => {
    if (status === "all") return orders.length;
    return orders.filter((o) => o.status.toLowerCase() === status.toLowerCase()).length;
  };

  const statusOptions = [
    { value: "all", label: "Tất cả", count: getOrderCountByStatus("all") },
    { value: "pending", label: "Chờ xác nhận", count: getOrderCountByStatus("pending") },
    { value: "confirmed", label: "Đã xác nhận", count: getOrderCountByStatus("confirmed") },
    { value: "shipping", label: "Đang giao", count: getOrderCountByStatus("shipping") },
    { value: "delivered", label: "Đã giao", count: getOrderCountByStatus("delivered") },
    { value: "cancelled", label: "Đã hủy", count: getOrderCountByStatus("cancelled") },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950 transition-colors duration-300">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 text-sm font-medium">Đang tải đơn hàng...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-gray-50 dark:bg-slate-950 transition-colors duration-300"
      style={{ 
        zoom: "0.8",
        MozTransform: "scale(0.8)",
        MozTransformOrigin: "top center"
      }}
    >
      <Navbar />
      <div className="py-4 sm:py-8 mt-16 sm:mt-24 lg:mt-28">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="mb-4 sm:mb-8">
            <h1 className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white font-inter">
              Đơn hàng của tôi
            </h1>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
              Quản lý và theo dõi tất cả đơn hàng của bạn
            </p>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm p-3 sm:p-5 mb-4 sm:mb-6 border border-gray-100 dark:border-slate-800">
            <div className="flex flex-col gap-3 sm:gap-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Tìm theo mã đơn hàng..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 sm:py-3 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm dark:text-white transition-all"
                />
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-[20px]">search</span>
              </div>

              <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar -mx-1 px-1">
                {statusOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setFilterStatus(option.value)}
                    className={`px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 ${
                      filterStatus === option.value
                        ? "bg-blue-600 text-white shadow-md shadow-blue-200 dark:shadow-none"
                        : "bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700"
                    }`}
                  >
                    {option.label}
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] ${
                      filterStatus === option.value ? "bg-white/20" : "bg-gray-200 dark:bg-slate-700"
                    }`}>
                      {option.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-3 sm:space-y-4">
            {filteredOrders.length === 0 ? (
              <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm p-10 text-center border border-gray-100 dark:border-slate-800">
                <span className="material-symbols-outlined text-[64px] text-gray-200 dark:text-slate-800 mb-4">inventory_2</span>
                <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-1">
                  Không tìm thấy đơn hàng
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-6">
                  {searchTerm ? "Thử thay đổi từ khóa tìm kiếm" : "Bạn chưa có đơn hàng nào"}
                </p>
                {!searchTerm && (
                  <Link
                    to="/products"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700 transition shadow-lg shadow-blue-100 dark:shadow-none"
                  >
                    Bắt đầu mua sắm
                  </Link>
                )}
              </div>
            ) : (
              filteredOrders.map((order) => (
                <div
                  key={order._id}
                  className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-800 overflow-hidden group hover:border-blue-200 dark:hover:border-blue-900/50 transition-all active:scale-[0.99]"
                >
                  <div className="bg-gray-50/50 dark:bg-slate-800/20 px-3 sm:px-5 py-3 border-b border-gray-100 dark:border-slate-800 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="p-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg">
                          <span className="material-symbols-outlined text-[18px]">receipt_long</span>
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-500 dark:text-slate-500 font-bold uppercase tracking-wider">Mã đơn</p>
                          <p className="text-xs sm:text-sm font-black text-gray-900 dark:text-white leading-none">#{order._id.slice(-8)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] text-gray-500 dark:text-slate-500 font-bold uppercase tracking-wider">Ngày đặt</p>
                        <p className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white leading-none">{formatDate(order.createdAt)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 sm:p-5">
                    <div className="space-y-3">
                      {order.items.map((item, index) => (
                        <div key={index} className="flex gap-3 sm:gap-4 items-start pb-3 border-b border-gray-50 dark:border-slate-800/50 last:border-b-0 last:pb-0">
                          <div className="w-14 h-14 sm:w-16 sm:h-16 relative flex-shrink-0">
                            <img
                              src={item.image}
                              alt={item.name}
                              onError={(e) => {
                                e.target.onerror = null; 
                                e.target.src = "https://cdn-icons-png.flaticon.com/512/679/679821.png"; // Gift icon fallback
                              }}
                              className="w-full h-full object-cover rounded-xl border border-gray-100 dark:border-slate-800"
                            />
                            {(item.isGift || (item.name && item.name.toLowerCase().includes("quà tặng"))) && (
                              <div className="absolute -top-1 -right-1 bg-blue-600 text-white rounded-full p-0.5 shadow-sm">
                                <span className="material-symbols-outlined text-[10px]">redeem</span>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-bold text-gray-900 dark:text-white text-xs sm:text-sm line-clamp-1 leading-tight mb-0.5">
                              {item.name}
                            </h4>
                            <p className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-400 font-medium">
                              Màu: {item.selectedColor} | Size: {item.selectedSize}
                            </p>
                            <div className="flex items-center justify-between mt-1">
                              <p className="text-[11px] sm:text-xs text-gray-600 dark:text-gray-400 font-bold">SL: x{item.qty}</p>
                              <p className="font-black text-blue-600 dark:text-blue-400 text-xs sm:text-sm">
                                {formatPrice(item.price * item.qty)}₫
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-slate-800">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <span className={`px-2.5 py-1 rounded-full text-[10px] font-black border uppercase tracking-wider ${getStatusColor(order.status)}`}>
                            {getStatusText(order.status)}
                          </span>
                          <span className="text-[10px] text-gray-400 dark:text-gray-500 font-bold">
                            {order.paymentMethod}
                          </span>
                        </div>

                        <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4">
                          <div className="text-right">
                             <p className="text-[9px] text-gray-500 dark:text-slate-500 font-bold uppercase tracking-wider mb-0.5">Tổng cộng</p>
                             <p className="text-lg sm:text-xl font-black text-red-600 dark:text-red-500 leading-none">
                                {formatPrice(order.total)}₫
                             </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2 sm:gap-3 mt-4">
                        <Link
                          to={`/order-success/${order._id}`}
                          className="flex-1 px-4 py-2 border border-blue-600 text-blue-600 dark:text-blue-400 rounded-xl font-bold text-[11px] sm:text-xs hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all text-center flex items-center justify-center gap-1.5"
                        >
                          <span className="material-symbols-outlined text-[16px]">visibility</span>
                          Chi tiết
                        </Link>

                        {order.status.toLowerCase() === "pending" && (
                          <button
                            onClick={() => handleOpenCancelModal(order)}
                            className="px-4 py-2 border border-red-200 dark:border-red-900/30 text-red-600 dark:text-red-500 rounded-xl font-bold text-[11px] sm:text-xs hover:bg-red-50 dark:hover:bg-red-900/20 transition-all flex items-center gap-1.5"
                          >
                            <span className="material-symbols-outlined text-[16px]">cancel</span>
                            Hủy đơn
                          </button>
                        )}

                        {order.status.toLowerCase() === "delivered" && (
                          <button className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-xl font-bold text-[11px] sm:text-xs hover:bg-blue-700 transition-all shadow-md shadow-blue-100 dark:shadow-none flex items-center justify-center gap-1.5">
                            <span className="material-symbols-outlined text-[16px]">shopping_cart</span>
                            Mua lại
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {showCancelModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleCloseCancelModal}></div>
          <div className="bg-white dark:bg-slate-900 rounded-3xl w-full max-w-md overflow-hidden relative shadow-2xl animate-modalUp border border-gray-100 dark:border-slate-800">
            <div className="p-4 bg-red-600 text-white flex justify-between items-center">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <span className="material-symbols-outlined">cancel</span>
                Hủy đơn hàng
              </h3>
              <button onClick={handleCloseCancelModal} className="hover:rotate-90 transition-transform">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="p-5 sm:p-6">
              <div className="bg-gray-50 dark:bg-slate-800/50 rounded-2xl p-4 mb-5 border border-gray-100 dark:border-slate-800 transition-colors">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-gray-500 dark:text-slate-400">Mã đơn hàng</span>
                  <span className="text-sm font-black text-gray-900 dark:text-white">#{selectedOrder?._id.slice(-8)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500 dark:text-slate-400">Tổng tiền</span>
                  <span className="text-base font-black text-red-600 dark:text-red-500">{formatPrice(selectedOrder?.total)}₫</span>
                </div>
              </div>

              <div className="mb-5">
                <label className="block text-xs sm:text-sm font-black text-gray-700 dark:text-gray-300 mb-3 ml-1">
                  Lý do hủy đơn <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {cancelReasons.map((reason, index) => (
                    <label
                      key={index}
                      className={`flex items-center p-2.5 sm:p-3 border-2 rounded-xl cursor-pointer transition-all ${
                        cancelReason === reason
                          ? "border-red-500 dark:border-red-600 bg-red-50 dark:bg-red-900/10"
                          : "border-gray-100 dark:border-slate-800 hover:border-red-200 dark:hover:border-slate-700"
                      }`}
                    >
                      <input
                        type="radio"
                        name="cancelReason"
                        value={reason}
                        checked={cancelReason === reason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        className="w-4 h-4 text-red-600 focus:ring-red-500"
                      />
                      <span className="ml-3 text-xs sm:text-sm text-gray-700 dark:text-gray-300 font-medium">{reason}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/10 p-3 rounded-xl border border-yellow-200 dark:border-yellow-900/30 flex gap-2 mb-6">
                <span className="material-symbols-outlined text-yellow-600 dark:text-yellow-500 text-[18px]">info</span>
                <p className="text-[10px] sm:text-xs text-yellow-800 dark:text-yellow-500 leading-relaxed font-medium">
                  Hành động này không thể hoàn tác. Bạn chắc chắn muốn hủy đơn chứ?
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleCloseCancelModal}
                  disabled={cancellingOrder}
                  className="flex-1 py-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 font-bold text-xs sm:text-sm transition-all"
                >
                  Đóng
                </button>
                <button
                  onClick={handleCancelOrder}
                  disabled={!cancelReason || cancellingOrder}
                  className="flex-[2] py-3 bg-red-600 text-white rounded-xl font-bold text-xs sm:text-sm hover:bg-red-700 active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-red-100 dark:shadow-none"
                >
                  {cancellingOrder ? (
                    <span className="material-symbols-outlined animate-spin">refresh</span>
                  ) : (
                    "Xác nhận hủy"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersPage;
